import { InfoCircleOutlined } from '@ant-design/icons';
import { Checkbox, Col, Row, Tooltip } from 'antd';
import { getApi, postApi, putApi } from 'apis';
import Buttons from 'components/Global/Buttons';
import InputField from 'components/Global/InputField';
import SelectDropdown from 'components/Global/SelectDropdown';
import { FC, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchConstantDropdownAction } from '../../../../redux/actions/constantDropdownAction';
import { AppDispatch } from 'redux/store';
import { hasFormError, toastText, validateFormData } from 'utils/utils';
import {
	CoaAccountType,
	CoaMappingOptions,
	CoaRecord,
	CoaUpsertPayload,
} from '../types';
import styles from './index.module.scss';

interface DepartmentDropdownOption {
	label: string;
	value: string;
	branchId: string;
}

interface CoaFormState {
	accountNo: string;
	name: string;
	type: CoaAccountType | '';
	desc: string;
	branchIds: string[];
	departmentIds: string[];
	earningDescriptions: string[];
	deductionDescriptions: string[];
	useTotalEarnings: boolean;
	isNetPayAccount: boolean;
}

const emptyFormState = (): CoaFormState => ({
	accountNo: '',
	name: '',
	type: '',
	desc: '',
	branchIds: [],
	departmentIds: [],
	earningDescriptions: [],
	deductionDescriptions: [],
	useTotalEarnings: false,
	isNetPayAccount: false,
});

const accountTypeOptions = [
	{ label: 'DEBIT', value: 'DEBIT' },
	{ label: 'CREDIT', value: 'CREDIT' },
];

interface CoaMappingTooltipContentProps {
	title: string;
	description: string;
	points: string[];
}

const CoaMappingTooltipContent = ({
	title,
	description,
	points,
}: CoaMappingTooltipContentProps) => (
	<div className={styles['mapping-tooltip']}>
		<p className={styles['mapping-tooltip__title']}>{title}</p>
		<p className={styles['mapping-tooltip__description']}>{description}</p>
		<ul className={styles['mapping-tooltip__list']}>
			{points.map((point) => (
				<li key={point}>{point}</li>
			))}
		</ul>
	</div>
);

const TOTAL_EARNINGS_TOOLTIP = (
	<CoaMappingTooltipContent
		title="Total Earnings Account"
		description="Maps this account to payroll earnings in aggregate."
		points={[
			'Uses combined total earnings for all employees in the selected branch and department scope.',
			'Individual earning types are not selected when this option is enabled.',
		]}
	/>
);

const NET_PAY_TOOLTIP = (
	<CoaMappingTooltipContent
		title="Net Pay Account"
		description="Maps this account to the net pay balance for journal posting."
		points={[
			'Net pay equals total earnings minus deductions mapped to other chart of accounts.',
		]}
	/>
);

const infoIconStyle = {
	marginLeft: '0.8rem',
	color: '#584495',
	cursor: 'pointer' as const,
};

const mappingTooltipProps = {
	placement: 'right' as const,
	overlayClassName: styles['mapping-tooltip-overlay'],
	color: '#fff' as const,
};

interface AddCoaBodyProps {
	closeDrawerByAnimation: () => void;
	fetchCoa: () => void;
	editSelected: CoaRecord | null;
}

const AddCoaBody: FC<AddCoaBodyProps> = ({
	closeDrawerByAnimation,
	fetchCoa,
	editSelected,
}) => {
	const dispatch = useDispatch<AppDispatch>();
	const dropdownOptions = useSelector(
		(state: { constantDropdown: { data: Record<string, unknown> | null } }) =>
			state.constantDropdown.data,
	);
	const { isLoading: isDropdownLoading } = useSelector(
		(state: { constantDropdown: { isLoading: boolean } }) =>
			state.constantDropdown,
	);

	const [loading, setLoading] = useState(false);
	const [formValues, setFormValues] = useState<CoaFormState>(emptyFormState);
	const [mappingOptions, setMappingOptions] = useState<CoaMappingOptions>({
		earnings: [],
		deductions: [],
	});
	const [formError, setFormError] = useState<Record<string, boolean>>({
		accountNo: false,
		name: false,
		type: false,
		desc: false,
	});

	const branchOptions = useMemo(
		() =>
			(
				(dropdownOptions?.branchCode as {
					label: string;
					value: string;
				}[]) ?? []
			).filter((option) => option.value),
		[dropdownOptions?.branchCode],
	);

	const allDepartments = useMemo(
		() =>
			(
				(dropdownOptions?.department as DepartmentDropdownOption[]) ?? []
			).filter((option) => option.value),
		[dropdownOptions?.department],
	);

	const departmentOptions = useMemo(() => {
		if (!formValues.branchIds.length) {
			return [];
		}

		return allDepartments.filter((dept) =>
			formValues.branchIds.includes(dept.branchId),
		);
	}, [formValues.branchIds, allDepartments]);

	useEffect(() => {
		dispatch(fetchConstantDropdownAction({}));
	}, [dispatch]);

	useEffect(() => {
		const loadMappingOptions = async () => {
			try {
				const mappingRes = await getApi('/coa/mapping-options');
				setMappingOptions(mappingRes.data.data as CoaMappingOptions);
			} catch (err: unknown) {
				const message =
					(err as { response?: { data?: { message?: string } } })?.response
						?.data?.message || 'Failed to load mapping options';
				toastText(message, 'error');
			}
		};
		loadMappingOptions();
	}, []);

	useEffect(() => {
		if (editSelected) {
			setFormValues({
				accountNo: editSelected.accountNo ?? '',
				name: editSelected.name,
				type: editSelected.type,
				desc: editSelected.desc ?? '',
				branchIds: editSelected.branchIds,
				departmentIds: editSelected.departmentIds,
				earningDescriptions: editSelected.earningDescriptions,
				deductionDescriptions: editSelected.deductionDescriptions,
				useTotalEarnings: editSelected.useTotalEarnings,
				isNetPayAccount: editSelected.isNetPayAccount,
			});
			setFormError({
				accountNo: false,
				name: false,
				type: false,
				desc: false,
			});
		} else {
			setFormValues(emptyFormState());
			setFormError({
				accountNo: false,
				name: false,
				type: false,
				desc: false,
			});
		}
	}, [editSelected]);

	const handleChange = (
		value: string | string[] | boolean,
		key: keyof CoaFormState,
		required = false,
	) => {
		setFormValues((prev) => ({ ...prev, [key]: value }));

		if (required && typeof value === 'string') {
			setFormError((prev) =>
				validateFormData({ [key]: value }, { ...prev }),
			);
		}
	};

	const handleBranchChange = (value: string | string[] | number) => {
		const branchIds = Array.isArray(value)
			? value.map(String).filter(Boolean)
			: value
				? [String(value)]
				: [];

		setFormValues((prev) => {
			const validDepartmentIds = allDepartments
				.filter((dept) => branchIds.includes(dept.branchId))
				.map((dept) => dept.value);

			const departmentIds =
				branchIds.length === 0
					? []
					: prev.departmentIds.filter((deptId) =>
						validDepartmentIds.includes(deptId),
					);

			return { ...prev, branchIds, departmentIds };
		});
	};

	const handleDepartmentChange = (value: string | string[] | number) => {
		const departmentIds = Array.isArray(value)
			? value.map(String).filter(Boolean)
			: value
				? [String(value)]
				: [];
		setFormValues((prev) => ({ ...prev, departmentIds }));
	};

	const handleSubmit = async () => {
		const checkFormError = validateFormData(
			{
				accountNo: formValues.accountNo,
				name: formValues.name,
				type: formValues.type,
				desc: formValues.desc,
			},
			{ ...formError },
		);
		setFormError(checkFormError);

		if (hasFormError(checkFormError)) {
			return;
		}

		const accountType = formValues.type;
		if (accountType !== 'DEBIT' && accountType !== 'CREDIT') {
			return;
		}

		const payload: CoaUpsertPayload = {
			accountNo: formValues.accountNo.trim(),
			name: formValues.name.trim(),
			type: accountType,
			desc: formValues.desc.trim(),
			branchIds: formValues.branchIds,
			departmentIds: formValues.departmentIds,
			earningDescriptions:
				formValues.useTotalEarnings || formValues.isNetPayAccount
					? []
					: formValues.earningDescriptions,
			deductionDescriptions:
				formValues.useTotalEarnings || formValues.isNetPayAccount
					? []
					: formValues.deductionDescriptions,
			useTotalEarnings: formValues.useTotalEarnings,
			isNetPayAccount: formValues.isNetPayAccount,
		};

		try {
			setLoading(true);
			if (editSelected) {
				const res = await putApi(`/coa/${editSelected.id}`, payload);
				toastText(res.data.message || 'COA updated successfully', 'success');
			} else {
				const res = await postApi('/coa', payload);
				toastText(res.data.message || 'COA created successfully', 'success');
			}
			fetchCoa();
			closeDrawerByAnimation();
		} catch (err: unknown) {
			const error = err as {
				response?: { data?: { message?: string; errorDescription?: string } };
			};
			toastText(
				error?.response?.data?.message ||
				error?.response?.data?.errorDescription ||
				'Failed to save COA account',
				'error',
			);
		} finally {
			setLoading(false);
		}
	};

	const buttons = [
		{
			text: 'Cancel',
			isLoading: false,
			className: 'btn-cancel',
			isSubmit: true,
			disabled: loading,
			onclick: closeDrawerByAnimation,
			fontSize: '1.8rem',
			minWidth: '12rem',
			minHeight: '4rem',
		},
		{
			text: 'Save',
			isLoading: loading,
			className: 'btn-blue',
			isSubmit: true,
			disabled: false,
			fontSize: '1.8rem',
			minWidth: '12rem',
			minHeight: '4rem',
			onclick: () => handleSubmit(),
		},
	];

	const earningSelectOptions = mappingOptions.earnings.map((e) => ({
		label: e.label,
		value: e.description,
	}));

	const deductionSelectOptions = mappingOptions.deductions.map((d) => ({
		label: d.label,
		value: d.description,
	}));

	return (
		<div className={styles['coa-form']}>
			<p className={styles['form-container-head-warning']}>
				<b>
					<sup>*</sup>
				</b>{' '}
				Indicated mandatory fields
			</p>
			<div className={styles['side-drawer-body']}>
				<Row gutter={5} className="margin-top-10">
					<Col span={24}>
						<InputField
							name="accountNo"
							value={formValues.accountNo}
							label="Account Number"
							required={true}
							helperText="Account number is required"
							onChange={(value) => handleChange(value, 'accountNo', true)}
							isError={formError.accountNo}
						/>
					</Col>
				</Row>
				<Row gutter={5} className="margin-top-10">
					<Col span={24}>
						<InputField
							name="name"
							value={formValues.name}
							label="COA Name"
							required={true}
							helperText="COA name is required"
							onChange={(value) => handleChange(value, 'name', true)}
							isError={formError.name}
						/>
					</Col>
				</Row>
				<Row gutter={5} className="margin-top-10">
					<Col span={24}>
						<SelectDropdown
							defaultOption={true}
							defaultLabel="Select Type"
							placeholder="Select Type"
							options={accountTypeOptions}
							value={formValues.type || null}
							onChange={(value) => {
								const type = String(value) as CoaAccountType | '';
								setFormValues((prev) => ({
									...prev,
									type,
									...(type !== 'CREDIT'
										? { isNetPayAccount: false }
										: {}),
								}));
								setFormError((prev) =>
									validateFormData({ type }, { ...prev }),
								);
							}}
							size="large"
							required={true}
							helperText="Type is required"
							label="Type"
							isError={formError.type}
						/>
					</Col>
				</Row>
				<Row gutter={5} className="margin-top-10">
					<Col span={24}>
						<InputField
							name="desc"
							value={formValues.desc}
							label="Journal Description"
							required={true}
							helperText="Journal description is required"
							onChange={(value) => handleChange(value, 'desc', true)}
							isError={formError.desc}
						/>
					</Col>
				</Row>
				<Row gutter={5} className="margin-top-10">
					<Col span={24}>
						<SelectDropdown
							defaultOption={false}
							placeholder="Select Branch"
							options={branchOptions}
							value={formValues.branchIds}
							mode="multiple"
							onChange={handleBranchChange}
							size="large"
							required={false}
							helperText="Select branch first"
							label="Branches (COA scope)"
							isError={false}
							loading={isDropdownLoading}
							disabled={isDropdownLoading}
						/>
					</Col>
				</Row>
				<Row gutter={5} className="margin-top-10">
					<Col span={24}>
						<SelectDropdown
							defaultOption={false}
							defaultLabel="Select Department"
							placeholder="Select Department"
							options={departmentOptions}
							value={formValues.departmentIds}
							mode="multiple"
							onChange={handleDepartmentChange}
							size="large"
							required={false}
							helperText="Select department(s) for selected branch(es)"
							label="Departments (COA scope)"
							disabled={
								isDropdownLoading || formValues.branchIds.length === 0
							}
							isError={false}
							loading={isDropdownLoading}
						/>
					</Col>
				</Row>
				{!formValues.isNetPayAccount && (
					<Row gutter={5} className="margin-top-10">
						<Col span={24}>
							<div className={styles['checkbox-row']}>
								<Checkbox
									checked={formValues.useTotalEarnings}
									disabled={formValues.isNetPayAccount}
									onChange={(e) => {
										const checked = e.target.checked;
										setFormValues((prev) => ({
											...prev,
											useTotalEarnings: checked,
											isNetPayAccount: false,
											earningDescriptions: checked
												? []
												: prev.earningDescriptions,
											deductionDescriptions: checked
												? []
												: prev.deductionDescriptions,
										}));
									}}
								>
									<span className={styles['checkbox-label']}>
										Use Total Earnings
										<Tooltip
											title={TOTAL_EARNINGS_TOOLTIP}
											{...mappingTooltipProps}
										>
											<InfoCircleOutlined
												style={infoIconStyle}
												onClick={(e) => e.stopPropagation()}
											/>
										</Tooltip>
									</span>
								</Checkbox>
							</div>
						</Col>
					</Row>
				)}
				{!formValues.useTotalEarnings &&
					!formValues.isNetPayAccount && (
						<Row gutter={5} className="margin-top-10">
							<Col span={24}>
								<SelectDropdown
									defaultOption={false}
									placeholder="Select Earnings"
									options={earningSelectOptions}
									value={formValues.earningDescriptions}
									mode="multiple"
									onChange={(value) => {
										const descriptions = Array.isArray(value)
											? value.map(String)
											: value
												? [String(value)]
												: [];
										setFormValues((prev) => ({
											...prev,
											earningDescriptions: descriptions,
										}));
									}}
									size="large"
									required={false}
									helperText=""
									label="Earnings"
									isError={false}
								/>
							</Col>
						</Row>
					)}
				{formValues.type === 'CREDIT' &&
					!formValues.useTotalEarnings && (
						<Row gutter={5} className="margin-top-10">
							<Col span={24}>
								<div className={styles['checkbox-row']}>
									<Checkbox
										checked={formValues.isNetPayAccount}
										disabled={formValues.useTotalEarnings}
										onChange={(e) => {
											const checked = e.target.checked;
											setFormValues((prev) => ({
												...prev,
												isNetPayAccount: checked,
												useTotalEarnings: false,
												earningDescriptions: checked
													? []
													: prev.earningDescriptions,
												deductionDescriptions: checked
													? []
													: prev.deductionDescriptions,
											}));
										}}
									>
										<span
											className={styles['checkbox-label']}
										>
											This Account Represents Net Pay
											<Tooltip
												title={NET_PAY_TOOLTIP}
												{...mappingTooltipProps}
											>
												<InfoCircleOutlined
													style={infoIconStyle}
													onClick={(e) =>
														e.stopPropagation()
													}
												/>
											</Tooltip>
										</span>
									</Checkbox>
								</div>
							</Col>
						</Row>
					)}
				{!formValues.useTotalEarnings &&
					!formValues.isNetPayAccount && (
						<Row gutter={5} className="margin-top-10">
							<Col span={24}>
								<SelectDropdown
									defaultOption={false}
									placeholder="Select Deductions"
									options={deductionSelectOptions}
									value={formValues.deductionDescriptions}
									mode="multiple"
									onChange={(value) => {
										const descriptions = Array.isArray(
											value,
										)
											? value.map(String)
											: value
												? [String(value)]
												: [];
										setFormValues((prev) => ({
											...prev,
											deductionDescriptions: descriptions,
										}));
									}}
									size="large"
									required={false}
									helperText=""
									label="Deductions"
									isError={false}
								/>
							</Col>
						</Row>
					)}
				<div className={styles['buttons']}>
					<Buttons buttons={buttons} />
				</div>
			</div>
		</div>
	);
};

export default AddCoaBody;
